import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils/helpers'
import type { SidebarLogoProps } from './SidebarLogo.types'

/**
 * Backend sometimes returns a bare id / non-URL string for org logos.
 * Only treat real URL-like values as image sources (never e.g. "1234").
 */
function isUsableLogoUrl(value: string | null | undefined): value is string {
  const trimmed = value?.trim()
  if (!trimmed) return false
  return (
    /^https?:\/\//i.test(trimmed) ||
    trimmed.startsWith('/') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('blob:')
  )
}

/**
 * Sidebar logo block aligned with Angular layout (#logo_lg / #logo_small):
 * - expanded: org_logo (only when present)
 * - collapsed: org_swoosh_logo || org_logo (only when present)
 * No static Liberty fallback — matches Angular `*ngIf` on org logo fields.
 */
export function SidebarLogo({
  isExpanded,
  orgLogo,
  orgSwooshLogo,
  orgWebsite,
  alt = 'Organization',
  trailing,
  className,
}: SidebarLogoProps) {
  const resolvedOrgLogo = isUsableLogoUrl(orgLogo) ? orgLogo.trim() : null
  const resolvedOrgSwoosh = isUsableLogoUrl(orgSwooshLogo)
    ? orgSwooshLogo.trim()
    : null

  const preferredLogo = resolvedOrgLogo
  const preferredSwoosh = resolvedOrgSwoosh || resolvedOrgLogo

  const [logoSrc, setLogoSrc] = useState<string | null>(preferredLogo)
  const [swooshSrc, setSwooshSrc] = useState<string | null>(preferredSwoosh)

  useEffect(() => {
    setLogoSrc(preferredLogo)
  }, [preferredLogo])

  useEffect(() => {
    setSwooshSrc(preferredSwoosh)
  }, [preferredSwoosh])

  const images = (
    <>
      {logoSrc ? (
        <img
          src={logoSrc}
          alt={alt}
          className={cn(
            'h-8 w-auto max-w-full object-contain transition-all duration-300 ease-in-out',
            isExpanded ? 'block opacity-100' : 'hidden opacity-0',
          )}
          onError={() => setLogoSrc(null)}
        />
      ) : null}
      {swooshSrc ? (
        <img
          src={swooshSrc}
          alt={alt}
          className={cn(
            'h-8 w-8 object-contain transition-all duration-300 ease-in-out',
            isExpanded ? 'hidden opacity-0' : 'block opacity-100',
          )}
          onError={() => setSwooshSrc(null)}
        />
      ) : null}
    </>
  )

  const inner = (
    <div
      className={cn(
        'relative flex w-full items-center transition-all duration-300 ease-in-out',
        isExpanded && trailing ? 'justify-between gap-2' : 'justify-center',
      )}
    >
      {orgWebsite ? (
        <a
          href={orgWebsite}
          target="_blank"
          rel="noopener noreferrer"
          className="flex min-w-0 items-center justify-center"
          title={alt}
        >
          {images}
        </a>
      ) : (
        <div className="flex min-w-0 items-center justify-center">{images}</div>
      )}
      {isExpanded && trailing}
    </div>
  )

  return (
    <div
      className={cn(
        'flex h-[3.2rem] min-h-[3.2rem] max-h-[3.2rem] items-center justify-center border-b border-gray-200 bg-white px-4',
        className,
      )}
    >
      {inner}
    </div>
  )
}
